import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, MailPlus, Search, Trash2, X } from "lucide-react";

const inputCls = "w-full bg-surface border border-ink/20 focus:border-ember outline-none px-3 py-2.5 text-ink placeholder:text-ink/30 transition-colors text-sm";
const labelCls = "block text-[11px] uppercase tracking-[0.2em] text-ink/50 mb-1.5";

export default function AdminUsers({ onChanged }) {
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [pending, setPending] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "" });
  const [grantCourseId, setGrantCourseId] = useState("");
  const [pregrantEmail, setPregrantEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async (q = search) => {
    try {
      const { data } = await api.get("/admin/users", { params: { search: q } });
      setUsers(data);
      const [c, p] = await Promise.all([api.get("/admin/courses"), api.get("/admin/pending-grants")]);
      setCourses(c.data);
      setPending(p.data);
      if (!grantCourseId && c.data.length) setGrantCourseId(c.data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(""); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (u) => {
    setSelected(u.id);
    try {
      const { data } = await api.get(`/admin/users/${u.id}`);
      setDetail(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      await load();
      if (selected) {
        const { data } = await api.get(`/admin/users/${selected}`);
        setDetail(data);
      }
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const createUser = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post("/admin/users", { ...newUser, course_id: grantCourseId, grant: true });
      toast.success(data.email_sent ? "Compte créé, accès activé, email de bienvenue envoyé" : "Compte créé, accès activé (email non envoyé)");
      setNewUser({ name: "", email: "", password: "" });
    });
  };

  const pregrant = (e) => {
    e.preventDefault();
    run(async () => {
      await api.post("/admin/pregrant", { email: pregrantEmail, course_id: grantCourseId });
      setPregrantEmail("");
    }, "Email pré-activé");
  };

  const grant = (uid, cid) => run(() => api.post(`/admin/users/${uid}/access`, { course_id: cid }), "Accès accordé");
  const revoke = (uid, cid) => {
    if (!window.confirm("Révoquer cet accès ?")) return;
    run(() => api.delete(`/admin/users/${uid}/access/${cid}`), "Accès révoqué");
  };

  const statusBadge = (status) => status === "active"
    ? <span className="text-[11px] uppercase tracking-wider text-green-600">Actif</span>
    : <span className="text-[11px] uppercase tracking-wider text-red-500">Révoqué</span>;

  return (
    <div className="mt-10 grid lg:grid-cols-3 gap-8" data-testid="admin-users">
      <div className="lg:col-span-2 space-y-6">
        {/* SEARCH + LIST */}
        <div className="border border-ink/10 bg-surface">
          <div className="px-5 py-4 border-b border-ink/10 flex items-center gap-3">
            <Search size={15} className="text-ink/40" />
            <input
              data-testid="user-search-input"
              value={search}
              onChange={(e) => { setSearch(e.target.value); load(e.target.value); }}
              placeholder="Rechercher un utilisateur (nom ou email)…"
              className="flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-ink/30"
            />
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {users.length === 0 && <p className="px-5 py-10 text-ink/40 text-sm">Aucun utilisateur.</p>}
            {users.map((u) => (
              <button
                key={u.id}
                data-testid={`user-row-${u.id}`}
                onClick={() => openDetail(u)}
                className={`w-full text-left px-5 py-4 border-b border-ink/5 flex flex-wrap items-center gap-4 transition-colors ${selected === u.id ? "bg-ember/5" : "hover:bg-panel/50"}`}
              >
                <div className="flex-1 min-w-[180px]">
                  <p className="text-sm font-medium text-ink">{u.name || "—"}</p>
                  <p className="text-xs text-ink/45">{u.email}</p>
                </div>
                <p className="text-xs text-ink/40">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</p>
                <div className="text-xs">
                  {u.accesses.some((a) => a.status === "active")
                    ? <span className="text-ember font-medium">{u.accesses.filter((a) => a.status === "active").length} accès actif(s)</span>
                    : <span className="text-ink/35">Sans accès</span>}
                </div>
              </button>
            ))}
          </div>
          {pending.length > 0 && (
            <div className="px-5 py-4 bg-panel/50 border-t border-ink/10">
              <p className="text-[11px] uppercase tracking-[0.2em] text-ink/40 mb-2">Emails pré-activés (en attente d'inscription)</p>
              <div className="flex flex-wrap gap-2">
                {pending.map((p) => (
                  <span key={p.email} className="inline-flex items-center gap-2 text-xs bg-surface border border-ink/15 px-3 py-1.5 text-ink/70" data-testid={`pending-${p.email}`}>
                    {p.email}
                    <button onClick={async () => { await api.delete(`/admin/pregrant/${p.email}`); load(); }} className="text-ink/35 hover:text-red-500"><Trash2 size={12} /></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* USER DETAIL */}
        {detail && (
          <div className="border border-ink/10 bg-surface p-6" data-testid="user-detail">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-display font-medium text-lg text-ink">{detail.name || detail.email}</h3>
                <p className="text-sm text-ink/50">{detail.email} — inscrit le {detail.created_at ? new Date(detail.created_at).toLocaleDateString() : "—"}</p>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-ink/40 hover:text-ink"><X size={16} /></button>
            </div>

            <h4 className="mt-6 mb-3 text-xs uppercase tracking-[0.25em] text-ink/40">Accès aux formations</h4>
            <div className="space-y-2">
              {courses.map((c) => {
                const acc = detail.accesses.find((a) => a.course_id === c.id);
                const active = acc?.status === "active";
                return (
                  <div key={c.id} className="flex flex-wrap items-center gap-3 border border-ink/10 px-4 py-3" data-testid={`detail-access-${c.id}`}>
                    <span className="flex-1 text-sm text-ink/80 min-w-[160px]">{c.title_fr}</span>
                    {acc ? statusBadge(acc.status) : <span className="text-[11px] uppercase tracking-wider text-ink/35">Aucun</span>}
                    {acc?.activated_at && <span className="text-xs text-ink/40">depuis le {new Date(acc.activated_at).toLocaleDateString()}</span>}
                    {active ? (
                      <button onClick={() => revoke(detail.id, c.id)} data-testid={`revoke-access-${c.id}`} className="text-xs px-3 py-1.5 border border-red-300 text-red-500 hover:bg-red-50 transition-colors">Révoquer</button>
                    ) : (
                      <button onClick={() => grant(detail.id, c.id)} data-testid={`grant-access-${c.id}`} className="text-xs px-3 py-1.5 border border-ember text-ember hover:bg-ember hover:text-white transition-colors">Accorder l'accès</button>
                    )}
                  </div>
                );
              })}
            </div>

            {detail.payments.length > 0 && (
              <>
                <h4 className="mt-6 mb-3 text-xs uppercase tracking-[0.25em] text-ink/40">Paiements manuels</h4>
                <div className="space-y-2">
                  {detail.payments.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center gap-3 text-xs text-ink/60 border border-ink/10 px-4 py-2.5" data-testid={`detail-payment-${p.id}`}>
                      <span>{new Date(p.created_at).toLocaleDateString()}</span>
                      <span className="font-medium text-ink">{p.amount}€</span>
                      <span className="uppercase">{p.method}</span>
                      <span className={p.status === "confirmed" ? "text-green-600" : p.status === "rejected" ? "text-red-500" : "text-amber-600"}>{p.status}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* SIDE FORMS */}
      <div className="space-y-6">
        <form onSubmit={createUser} className="border border-ink/10 bg-surface p-6" data-testid="admin-create-user-form">
          <h3 className="font-display font-medium text-ink flex items-center gap-2"><UserPlus size={16} className="text-ember" /> Créer un compte client</h3>
          <p className="mt-2 text-xs text-ink/50 leading-relaxed">Pour un paiement hors ligne : créez le compte avec un mot de passe temporaire, l'accès est activé immédiatement.</p>
          <div className="mt-5 space-y-4">
            <div><label className={labelCls}>Nom complet</label><input data-testid="create-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} required className={inputCls} /></div>
            <div><label className={labelCls}>Email</label><input data-testid="create-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} required className={inputCls} /></div>
            <div><label className={labelCls}>Mot de passe temporaire</label><input data-testid="create-user-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required minLength={6} className={inputCls} /></div>
            <div>
              <label className={labelCls}>Formation à accorder</label>
              <select data-testid="create-user-course" value={grantCourseId} onChange={(e) => setGrantCourseId(e.target.value)} className={inputCls}>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title_fr}</option>)}
              </select>
            </div>
            <button data-testid="create-user-submit" disabled={busy} className="btn-glow w-full bg-ink hover:bg-ember disabled:opacity-50 text-white px-4 py-3 text-sm transition-colors">Créer + donner l'accès</button>
          </div>
        </form>

        <form onSubmit={pregrant} className="border border-ink/10 bg-surface p-6" data-testid="admin-pregrant-form">
          <h3 className="font-display font-medium text-ink flex items-center gap-2"><MailPlus size={16} className="text-ember" /> Pré-activer un email</h3>
          <p className="mt-2 text-xs text-ink/50 leading-relaxed">La personne créera son compte elle-même : l'accès sera activé automatiquement à son inscription.</p>
          <div className="mt-5 space-y-4">
            <input data-testid="pregrant-email" type="email" value={pregrantEmail} onChange={(e) => setPregrantEmail(e.target.value)} required placeholder="email@exemple.com" className={inputCls} />
            <button data-testid="pregrant-submit" disabled={busy} className="w-full border border-ember text-ember hover:bg-ember hover:text-white disabled:opacity-50 px-4 py-3 text-sm transition-colors">Pré-activer</button>
          </div>
        </form>
      </div>
    </div>
  );
}
